import emailjs from '@emailjs/browser';
import { useEffect, useState } from 'react';
import AdminLayout from '../../Components/Admin/AdminLayout';
import { Firebase, firebase } from '../../firebase/config';

function AdminEscrowLedger() {
  const [escrowList, setEscrowList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    // 1. Live stream transaction data
    const unsubscribeEscrow = Firebase.firestore()
      .collection('transactions')
      .where('status', 'in', ['delivered', 'completed', 'escrow_held'])
      .onSnapshot(async (snapshot) => {
        const db = Firebase.firestore();

        const promises = snapshot.docs.map(async (doc) => {
          const data = doc.data();
          const total = Number(data.totalAmount || 0);

          let productSellerDetails = {
            name: data.sellerName || 'ClubLoop Merchant',
            phone: 'N/A',
            email: 'help@clubloop.com',
            addressLine: 'No pickup address logged',
            city: 'Bengaluru',
            pincode: '560102',
            state: 'Karnataka'
          };

          if (data.productId) {
            try {
              const prodDoc = await db.collection('products').doc(data.productId).get();
              if (prodDoc.exists) {
                const prodData = prodDoc.data();
                const targetUserId = prodData.userId || data.sellerId;

                let resolvedSellerName = data.sellerName || 'ClubLoop Merchant';

                if (targetUserId) {
                  try {
                    const userProfileDoc = await db.collection('users').doc(targetUserId).get();
                    if (userProfileDoc.exists) {
                      const userData = userProfileDoc.data();
                      resolvedSellerName = userData.name || userData.username || userData.displayName || resolvedSellerName;
                    }
                  } catch (userFetchErr) {
                    console.warn(`Could not cross-resolve user profile data for UID: ${targetUserId}`, userFetchErr);
                  }
                }

                productSellerDetails = {
                  name: resolvedSellerName,
                  phone: prodData.sellerPhone || prodData.sellerDetails?.phone || 'N/A',
                  email: prodData.sellerEmail || prodData.sellerDetails?.email || 'help@clubloop.com',
                  addressLine: prodData.pickupAddress || prodData.sellerDetails?.addressLine || 'No address logged',
                  city: prodData.location?.city || prodData.sellerCity || 'Bengaluru',
                  pincode: prodData.location?.pincode || prodData.sellerPincode || '560102',
                  state: prodData.location?.state || prodData.sellerState || 'Karnataka'
                };
              }
            } catch (err) {
              console.warn(`Failed to resolve original product fields for lookup id: ${data.productId}`, err);
            }
          }

          return {
            id: doc.id,
            productId: data.productId,
            productName: data.productName || 'Golf Equipment',
            totalAmount: total,
            commission: data.commission ? Number(data.commission) : total * 0.10,
            sellerPayout: data.sellerPayout ? Number(data.sellerPayout) : total * 0.90,
            sellerId: data.sellerId || 'N/A',
            payoutReleased: data.payoutReleased || false,
            status: data.status,
            payoutMethod: data.payoutMethod || null,
            paymentMethods: data.paymentMethods || [],
            emailAlertSentAt: data.emailAlertSentAt ? data.emailAlertSentAt.toDate() : null,
            sellerDetails: productSellerDetails,
            shippingAddress: data.shippingAddress || {
              name: 'N/A',
              phone: 'N/A',
              addressLine: 'No shipping data logged',
              city: '',
              pincode: '',
              state: ''
            },
            shippingStatus: data.shippingStatus || 'pending_payment',
            shiprocketOrderId: data.shiprocketOrderId || null,
            shiprocketShipmentId: data.shiprocketShipmentId || null
          };
        });

        const resolvedList = await Promise.all(promises);
        resolvedList.sort((a, b) => b.id.localeCompare(a.id));
        setEscrowList(resolvedList);
        setLoading(false);
      }, (error) => {
        console.error("Error streaming escrow history records from Firestore:", error);
        setLoading(false);
      });

    // ⏳ 2. SCHEDULED INTERVAL SCANNER: Admin Only Alerts
    const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;
    const checkAndSendIntervalEmails = async () => {
      console.log("⏱️ Running scheduled 8-hour pending payout email check for Admin...");
      try {
        const db = Firebase.firestore();
        const now = new Date();
        const snap = await db
          .collection('transactions')
          .where('status', '==', 'delivered')
          .where('payoutReleased', '==', false)
          .get();

        for (const doc of snap.docs) {
          const tx = doc.data();
          const lastSent = tx.emailAlertSentAt ? tx.emailAlertSentAt.toDate() : null;

          if (!lastSent || (now - lastSent) > EIGHT_HOURS_MS) {
            const sellerPayout = tx.sellerPayout ? Number(tx.sellerPayout) : Number(tx.totalAmount || 0) * 0.90;

            // ✉️ Admin Alert Email Only
            const adminParams = {
              txId: doc.id,
              txId_short: doc.id.slice(0, 8),
              sellerPayout: sellerPayout.toLocaleString('en-IN'),
              sellerName: tx.sellerName || 'ClubLoop Seller'
            };

            await emailjs.send('service_8mzvgo2', 'template_jk52kvo', adminParams, '-L1jz_3kn8zIPR0Uf ');

            // Apply Server Lock timestamp
            await db.collection('transactions').doc(doc.id).update({
              emailAlertSentAt: firebase.firestore.FieldValue.serverTimestamp()
            });
          }
        }
      } catch (err) {
        console.error("Scheduled polling loop check encountered an error:", err);
      }
    };

    checkAndSendIntervalEmails();
    const timerId = setInterval(checkAndSendIntervalEmails, EIGHT_HOURS_MS);

    return () => {
      unsubscribeEscrow();
      clearInterval(timerId);
    };
  }, []);

  const extractPayoutCredentials = (tx) => {
    if (tx.payoutMethod) {
      return {
        type: tx.payoutMethod.type,
        vpa: tx.payoutMethod.upiId || '',
        bankString: tx.payoutMethod.type === 'bank_transfer'
          ? `${tx.payoutMethod.accountHolderName || 'User'}, ${tx.payoutMethod.accountNumber || ''}, ${tx.payoutMethod.ifscCode || ''}`
          : ''
      };
    }
    return null;
  };

  const handleExecutePayout = async (txId, productId, sellerAmount, sellerId, txData) => {
    const verification = window.confirm(`Confirm manual payout clearance?\n\nVerify that you have physically transferred ₹${sellerAmount.toLocaleString('en-IN')}`);
    if (!verification) return;

    setProcessingId(txId);
    try {
      let sellerEmail = '';
      try {
        // Look up user collection data dynamically to verify destination email
        const userSnap = await Firebase.firestore().collection('users').doc(sellerId).get();
        if (userSnap.exists) { sellerEmail = userSnap.data().email || ''; }
      } catch (userErr) { console.warn(userErr); }

      const batch = Firebase.firestore().batch();
      batch.update(Firebase.firestore().collection('transactions').doc(txId), {
        payoutReleased: true,
        status: 'completed',
        payoutReleasedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      });

      if (productId) {
        batch.update(Firebase.firestore().collection('products').doc(productId), {
          status: 'completed',
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
      }
      await batch.commit();

      // 🔥 EXCLUSIVE DISPATCH STEP: Fires settlement confirmation notification to seller ONLY when clicked
      if (sellerEmail) {
        const releasedParams = {
          sellerEmail: sellerEmail,
          sellerName: txData.sellerDetails.name,
          txId_short: txId.slice(0, 8),
          sellerPayout: sellerAmount.toLocaleString('en-IN'),
          commission: txData.commission.toLocaleString('en-IN')
        };
        try {
          await emailjs.send('service_7s1vpg8', 'template_rylom3l', releasedParams, 'AUNDQqCVda-YJFpJA ');
          console.log(`✉️ Settlement confirmation email successfully processed and sent to seller: ${sellerEmail}`);
        } catch (err) {
          console.error("Failed to send wired execution alert to seller:", err);
        }
      }

      alert("Payout successfully processed!");
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const totalGross = escrowList.reduce((sum, tx) => sum + tx.totalAmount, 0);
  const totalCommission = escrowList.reduce((sum, tx) => sum + tx.commission, 0);
  const settledPayouts = escrowList.filter(tx => tx.payoutReleased).reduce((sum, tx) => sum + tx.sellerPayout, 0);
  const filteredList = escrowList.filter(tx => activeTab === 'pending' ? !tx.payoutReleased : tx.payoutReleased);

  return (
    <AdminLayout>
      <style>{`
        .ledger-cards-container { display: flex; flex-direction: column; gap: 16px; }
        .ledger-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
        .ledger-card-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
        .analytics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; margin-bottom: 24px; }
        .ledger-desktop-table { display: none; width: 100%; border-collapse: collapse; }
        .profile-badge-box { background-color: #f8fafc; border: 1px solid #edf2f7; padding: 8px 10px; border-radius: 6px; font-size: 12px; line-height: 1.4; color: #334155; }
        @media (min-width: 1200px) {
          .ledger-cards-container { display: none; }
          .ledger-desktop-table { display: table; }
        }
      `}</style>

      <div className="sec-hdr" style={{ marginBottom: '20px', padding: '0 4px' }}>
        <h1 className="sec-title" style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '6px' }}>
          Payment Release Control & Ledger
        </h1>
        <p className="sec-sub" style={{ color: '#666', fontSize: '0.9rem' }}>
          Review active transaction matches, vendor warehouse listings, and customer courier targets.
        </p>
      </div>

      <div className="analytics-grid">
        <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Total Marketplace Gross</span>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: '4px 0 0 0' }}>₹{totalGross.toLocaleString('en-IN')}</h2>
        </div>
        <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#166534', textTransform: 'uppercase' }}>Platform Commission (10%)</span>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#15803d', margin: '4px 0 0 0' }}>₹{totalCommission.toLocaleString('en-IN')}</h2>
        </div>
        <div style={{ padding: '16px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e40af', textTransform: 'uppercase' }}>Released Vendor Payouts</span>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1d4ed8', margin: '4px 0 0 0' }}>₹{settledPayouts.toLocaleString('en-IN')}</h2>
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', gap: '8px', marginBottom: '20px' }}>
        <button onClick={() => setActiveTab('pending')} style={{ padding: '12px 14px', fontSize: '14px', fontWeight: '600', backgroundColor: 'transparent', border: 'none', borderBottom: activeTab === 'pending' ? '3px solid #2a9d8f' : '3px solid transparent', color: activeTab === 'pending' ? '#2a9d8f' : '#718096', cursor: 'pointer' }}>
          ⏳ Pending ({escrowList.filter(t => !t.payoutReleased).length})
        </button>
        <button onClick={() => setActiveTab('completed')} style={{ padding: '12px 14px', fontSize: '14px', fontWeight: '600', backgroundColor: 'transparent', border: 'none', borderBottom: activeTab === 'completed' ? '3px solid #2a9d8f' : '3px solid transparent', color: activeTab === 'completed' ? '#2a9d8f' : '#718096', cursor: 'pointer' }}>
          ✅ Completed ({escrowList.filter(t => t.payoutReleased).length})
        </button>
      </div>

      {loading ? (
        <p>Loading ledger processing matrices...</p>
      ) : filteredList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 16px', color: '#a0aec0', fontStyle: 'italic', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          No records found.
        </div>
      ) : (
        <>
          {/* 📱 MOBILE CARDS CONTAINER */}
          <div className="ledger-cards-container">
            {filteredList.map((tx) => {
              const payoutCreds = extractPayoutCredentials(tx);
              const encodedName = encodeURIComponent(tx.sellerDetails.name);
              const intentUpiLink = payoutCreds?.type === 'upi' ? `upi://pay?pa=${payoutCreds.vpa}&pn=${encodedName}&am=${tx.sellerPayout}&cu=INR` : '';

              return (
                <div key={tx.id} className="ledger-card" style={{ backgroundColor: tx.payoutReleased ? '#fafdfc' : '#fff' }}>
                  <div className="ledger-card-row">
                    <div>
                      <span style={{ fontSize: '11px', color: '#718096', display: 'block' }}>ORDER REF & PRODUCT</span>
                      <strong style={{ fontFamily: 'monospace', color: '#007bff', fontSize: '14px' }}>#{tx.id.slice(0, 8)}</strong>
                      <span style={{ fontSize: '12px', color: '#334155', display: 'block', fontWeight: '500' }}>{tx.productName}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <strong style={{ color: '#2a9d8f', fontSize: '16px' }}>₹{tx.sellerPayout.toLocaleString('en-IN')}</strong>
                    </div>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '4px' }}>SELLER PROFILES (LIVE FROM LISTING FORM)</span>
                    <div className="profile-badge-box" style={{ borderLeft: '3px solid #2a9d8f' }}>
                      <strong>🧑 {tx.sellerDetails.name}</strong> • 📱 {tx.sellerDetails.phone}<br/>
                      ✉️ {tx.sellerDetails.email}<br/>
                      📍 {tx.sellerDetails.addressLine}, {tx.sellerDetails.city} - {tx.sellerDetails.pincode}
                    </div>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <span style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '4px' }}>CUSTOMER SHIPPING ADDRESS</span>
                    <div className="profile-badge-box" style={{ borderLeft: '3px solid #3b82f6' }}>
                      <strong>📦 {tx.shippingAddress.name}</strong> • 📱 {tx.shippingAddress.phone}<br/>
                      📍 {tx.shippingAddress.addressLine}, {tx.shippingAddress.city} {tx.shippingAddress.pincode ? `- ${tx.shippingAddress.pincode}` : ''}
                    </div>
                  </div>

                  {tx.shiprocketOrderId && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', background: '#f0fdf4', padding: '8px', borderRadius: '6px', border: '1px solid #ccfbf1' }}>
                        🚚 <strong>Shiprocket Order ID:</strong> <code>{tx.shiprocketOrderId}</code>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', borderTop: '1px solid #edf2f7', paddingTop: '10px' }}>
                    <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700', backgroundColor: tx.payoutReleased ? '#e6fffa' : '#feebc8', color: tx.payoutReleased ? '#234e52' : '#744210' }}>
                      {tx.payoutReleased ? '✅ SETTLED' : '⏳ PENDING'}
                    </span>
                    {!tx.payoutReleased && (
                      <button className="btn btn-green" onClick={() => handleExecutePayout(tx.id, tx.productId, tx.sellerPayout, tx.sellerId, tx)} style={{ padding: '6px 12px', fontSize: '12px' }}>
                        Confirm Wired
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 💻 DESKTOP DATA TABLE LAYOUT */}
          <table className="ledger-desktop-table">
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left', backgroundColor: '#f7fafc' }}>
                <th style={{ padding: '14px 12px' }}>ORDER REF</th>
                <th style={{ padding: '14px 12px' }}>VPA VALUE</th>
                <th style={{ padding: '14px 12px' }}>PRODUCT SELLER LINK (PICKUP)</th>
                <th style={{ padding: '14px 12px' }}>CUSTOMER PROFILE (SHIPPING)</th>
                <th style={{ padding: '14px 12px' }}>SETTLEMENT GATEWAY</th>
                <th style={{ padding: '14px 12px' }}>COURIER ENGINE</th>
                <th style={{ padding: '14px 12px' }}>MOBILE LINK</th>
                <th style={{ padding: '14px 12px' }}>STATUS</th>
                <th style={{ padding: '14px 12px' }}>EXECUTE OPERATIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((tx) => {
                const payoutCreds = extractPayoutCredentials(tx);
                const encodedName = encodeURIComponent(tx.sellerDetails.name);
                const intentUpiLink = payoutCreds?.type === 'upi' ? `upi://pay?pa=${payoutCreds.vpa}&pn=${encodedName}&am=${tx.sellerPayout}&cu=INR` : '';

                return (
                  <tr key={tx.id} style={{ borderBottom: '1px solid #edf2f7', backgroundColor: tx.payoutReleased ? '#fbfdfd' : 'transparent' }}>
                    <td style={{ padding: '14px 12px', fontFamily: 'monospace', fontWeight: 'bold', color: '#007bff' }}>
                      #{tx.id.slice(0, 8)}
                      <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal', fontFamily: 'sans-serif', marginTop: '4px' }}>{tx.productName}</div>
                    </td>
                    <td style={{ padding: '14px 12px', color: '#2a9d8f', fontWeight: '600', fontSize: '15px' }}>
                      ₹{tx.sellerPayout.toLocaleString('en-IN')}
                    </td>

                    <td style={{ padding: '14px 12px', maxWidth: '240px' }}>
                      <div className="profile-badge-box" style={{ borderLeft: '3px solid #2a9d8f' }}>
                        <strong style={{ color: '#1e293b' }}>🧑 {tx.sellerDetails.name}</strong>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>📱 {tx.sellerDetails.phone}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>✉️ {tx.sellerDetails.email}</div>
                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#475569', borderTop: '1px dashed #e2e8f0', paddingTop: '4px' }}>
                          📍 {tx.sellerDetails.addressLine}, {tx.sellerDetails.city} - {tx.sellerDetails.pincode}
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '14px 12px', maxWidth: '240px' }}>
                      <div className="profile-badge-box" style={{ borderLeft: '3px solid #3b82f6' }}>
                        <strong style={{ color: '#1e293b' }}>📦 {tx.shippingAddress.name}</strong>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>📱 {tx.shippingAddress.phone}</div>
                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#475569', borderTop: '1px dashed #e2e8f0', paddingTop: '4px' }}>
                          📍 {tx.shippingAddress.addressLine}, {tx.shippingAddress.city} {tx.shippingAddress.pincode ? `- ${tx.shippingAddress.pincode}` : ''}
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: '14px 12px', fontSize: '13px' }}>
                      {!payoutCreds ? (
                        <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>No details</span>
                      ) : (
                        <div style={{ backgroundColor: '#f7fafc', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '11px' }}>
                          <strong>{payoutCreds.type === 'upi' ? '⚡ UPI:' : '🏦 Bank:'}</strong> {payoutCreds.type === 'upi' ? payoutCreds.vpa : payoutCreds.bankString}
                        </div>
                      )}
                    </td>

                    <td style={{ padding: '14px 12px', fontSize: '13px' }}>
                      {tx.shiprocketOrderId ? (
                        <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #ccfbf1', padding: '6px', borderRadius: '6px', fontSize: '11px' }}>
                          Order ID: <strong>{tx.shiprocketOrderId}</strong>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '12px' }}>Syncing AWB...</span>
                      )}
                    </td>

                    <td style={{ padding: '14px 12px' }}>
                      {!tx.payoutReleased && intentUpiLink ? (
                        <a href={intentUpiLink} style={{ display: 'inline-block', backgroundColor: '#1e293b', color: '#ffffff', textDecoration: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>📱 Intent</a>
                      ) : <span style={{ color: '#a0aec0', fontSize: '12px' }}>N/A</span>}
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', backgroundColor: tx.payoutReleased ? '#e6fffa' : '#feebc8', color: tx.payoutReleased ? '#234e52' : '#744210' }}>
                        {tx.payoutReleased ? '✅ SETTLED' : '⏳ PENDING'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 12px' }}>
                      {!tx.payoutReleased ? (
                        <button className="btn btn-green" disabled={processingId === tx.id} onClick={() => handleExecutePayout(tx.id, tx.productId, tx.sellerPayout, tx.sellerId, tx)} style={{ padding: '6px 14px', fontSize: '13px', fontWeight: '600' }}>
                          Confirm Wired
                        </button>
                      ) : <span style={{ color: '#718096', fontStyle: 'italic' }}>Paid Out</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </AdminLayout>
  );
}

export default AdminEscrowLedger;
